import { tools, categoryLabels, findTool, type ToolMeta } from './tools/registry';
import {
  clearSelectedFiles,
  peekSelectedFiles,
  setPendingFiles,
  setSelectedFiles,
  routeFiles,
} from './lib/pending-files';
import { getTheme, setTheme } from './lib/theme';
import { seoPages, type SeoPage } from './generated/seo-pages';
import {
  getAnalyticsConsent,
  initMetrika,
  isMetrikaHost,
  setAnalyticsConsent,
  trackFileSelected,
  trackPageView,
  trackToolError,
} from './lib/metrika';
import { setupPostDownloadAction } from './ui/post-download-cta';

const BRAND_NAME = 'ПДФКА';
const SITE_ORIGIN = 'https://пдфка.рф';
const HOME_TITLE = 'ПДФ онлайн — объединить, сжать и конвертировать | ПДФКА';
const HOME_DESCRIPTION =
  'ПДФКА — ПДФ-инструменты онлайн: объединить, сжать, разделить, подписать и конвертировать PDF в Ворд, JPG, текст и обратно прямо в браузере.';
const HOME_H1 = 'ПДФ онлайн: объединить, сжать и конвертировать';
const HOME_LEAD =
  'ПДФ в Ворд, Ворд в ПДФ, фото в ПДФ, ПДФ в JPG, разделить и подписать ПДФ — всё в браузере, файлы остаются на этом устройстве.';

type BeforeInstallPromptChoice = {
  outcome: 'accepted' | 'dismissed';
  platform: string;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<BeforeInstallPromptChoice>;
};

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

export function mountApp(root: HTMLElement) {
  root.innerHTML = `
    <div class="min-h-screen flex flex-col">
      <header class="bg-slate-900/80 backdrop-blur sticky top-0 z-10">
        <div class="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <a href="/" class="flex items-center gap-2 font-bold text-lg">
            <span class="inline-block w-8 h-8 rounded-lg bg-brand text-white text-xs font-extrabold grid place-items-center">PDF</span>
            <span>${BRAND_NAME}</span>
          </a>
          <button type="button" class="install-button" data-install-app aria-label="Сохранить сайт" title="Сохранить сайт">
            <span class="install-button-icon" aria-hidden="true">＋</span>
            <span class="install-button-label" data-install-label>Сохранить</span>
          </button>
        </div>
      </header>
      <main id="view" class="flex-1 w-full"></main>
      <footer id="app-footer" class="border-t border-slate-800 mt-12">
        <div class="max-w-6xl mx-auto px-4 py-4 text-xs text-slate-500 flex flex-wrap gap-3 justify-between items-center">
          <span>© ${BRAND_NAME} — бесплатные PDF-инструменты онлайн: объединить, сжать, разделить и конвертировать PDF в браузере, без отправки файлов.</span>
          <div class="flex items-center gap-4">
            <button type="button" class="theme-toggle" data-theme-toggle aria-label="Включить тёмный дизайн" aria-pressed="false">
              <span class="theme-toggle-icon theme-toggle-sun" aria-hidden="true">☀</span>
              <span class="theme-toggle-icon theme-toggle-moon" aria-hidden="true">☾</span>
            </button>
            <a href="/privacy/" class="hover:text-slate-300">Конфиденциальность</a>
            <a href="#/settings" class="hover:text-slate-300">Данные</a>
          </div>
        </div>
      </footer>
      <div class="install-modal" data-install-modal hidden>
        <button type="button" class="install-modal-backdrop" data-install-close aria-label="Закрыть"></button>
        <section class="install-dialog" role="dialog" aria-modal="true" aria-labelledby="install-dialog-title">
          <div class="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 id="install-dialog-title" class="text-xl font-bold" data-install-title>Сохранить ${BRAND_NAME}</h2>
              <p class="mt-2 text-sm leading-relaxed text-slate-400" data-install-lead></p>
            </div>
            <button type="button" class="install-close" data-install-close aria-label="Закрыть">×</button>
          </div>
          <ol class="install-steps" data-install-steps></ol>
        </section>
      </div>
      <div class="analytics-consent" data-analytics-consent hidden>
        <section class="analytics-consent-panel" aria-labelledby="analytics-consent-title">
          <div class="min-w-0">
            <h2 id="analytics-consent-title" class="text-xs font-semibold text-slate-300">Cookies для статистики</h2>
            <p class="mt-0.5 text-[11px] leading-snug text-slate-500">
              Сайт использует Яндекс Метрику. Файлы не отправляются.
              <a href="/privacy/" class="text-slate-400 underline decoration-slate-700 underline-offset-2 hover:text-brand">Подробнее</a>
            </p>
          </div>
          <div class="analytics-consent-actions">
            <button type="button" class="analytics-accept" data-analytics-accept>Принять</button>
          </div>
        </section>
      </div>
    </div>
  `;

  setupThemeSwitch(root);
  setupInstallAction(root);
  setupPostDownloadAction(root);
  setupAnalyticsConsent(root);
  initMetrika();

  const view = root.querySelector<HTMLElement>('#view')!;
  const reportRoute = () => window.setTimeout(trackPageView, 0);

  const router = () => {
    const hash = location.hash.replace(/^#\/?/, '');
    if (hash === 'privacy') {
      setPrivacyMeta();
      void renderPrivacyRoute(view);
      reportRoute();
      return;
    }
    if (hash === 'settings') {
      setToolMeta('Данные', 'Настройки локальных данных ПДФКА.');
      void renderSettingsRoute(view);
      reportRoute();
      return;
    }
    if (hash) {
      const tool = findTool(hash);
      if (!tool) {
        setHomeMeta();
        renderHome(view);
        reportRoute();
        return;
      }
      setToolMeta(tool.title, tool.description);
      renderToolView(view, tool);
      reportRoute();
      return;
    }

    if (isPrivacyPath(location.pathname)) {
      setPrivacyMeta();
      void renderPrivacyRoute(view);
      reportRoute();
      return;
    }

    const seoPage = getSeoPageForCurrentPath();
    if (seoPage) {
      const tool = findTool(seoPage.appHash);
      if (tool) {
        setSeoMeta(seoPage);
        renderToolView(view, tool, seoPage);
        reportRoute();
        return;
      }
    }

    setHomeMeta();
    renderHome(view);
    reportRoute();
  };

  window.addEventListener('hashchange', router);
  window.addEventListener('popstate', router);
  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest<HTMLAnchorElement>('a[href]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.hash) return;
    if (url.pathname !== '/' && !getSeoPageForPath(url.pathname) && !isPrivacyPath(url.pathname)) return;
    event.preventDefault();
    history.pushState(null, '', url.pathname);
    router();
    window.scrollTo({ top: 0 });
  });
  router();
}

function getSeoPageForCurrentPath(): SeoPage | undefined {
  return getSeoPageForPath(location.pathname);
}

function getSeoPageForPath(pathname: string): SeoPage | undefined {
  const normalized = pathname.replace(/^\/+|\/+$/g, '');
  if (!normalized) return undefined;
  return seoPages.find((page) => page.slug === normalized);
}

function isPrivacyPath(pathname: string): boolean {
  return pathname.replace(/^\/+|\/+$/g, '') === 'privacy';
}

function setHomeMeta(): void {
  setDocumentMeta({
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    canonical: `${SITE_ORIGIN}/`,
  });
  setRouteStructuredData(homeStructuredData());
}

function setPrivacyMeta(): void {
  setDocumentMeta({
    title: `Политика конфиденциальности — ${BRAND_NAME}`,
    description: 'Политика конфиденциальности ПДФКА: PDF-файлы обрабатываются локально в браузере, а Яндекс Метрика используется для статистики посещаемости.',
    canonical: `${SITE_ORIGIN}/privacy/`,
  });
  setRouteStructuredData([
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Политика конфиденциальности',
      url: `${SITE_ORIGIN}/privacy/`,
      inLanguage: 'ru',
      description: 'Политика конфиденциальности ПДФКА: локальная обработка PDF-файлов и cookies статистики Яндекс Метрики.',
      publisher: {
        '@type': 'Organization',
        name: BRAND_NAME,
      },
    },
  ]);
}

function setSeoMeta(page: SeoPage): void {
  setDocumentMeta({
    title: page.title,
    description: page.description,
    canonical: `${SITE_ORIGIN}/${page.slug}/`,
  });
  setRouteStructuredData(toolStructuredData(page));
}

function setToolMeta(title: string, description: string): void {
  setDocumentMeta({
    title: `${title} — ${BRAND_NAME}`,
    description,
    canonical: `${SITE_ORIGIN}/`,
  });
  setRouteStructuredData(null);
}

function setDocumentMeta(next: { title: string; description: string; canonical: string }): void {
  document.title = next.title;
  setMeta('description', next.description);
  setMeta('twitter:title', next.title);
  setMeta('twitter:description', next.description);
  setPropertyMeta('og:title', next.title);
  setPropertyMeta('og:description', next.description);
  setPropertyMeta('og:url', next.canonical);
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = next.canonical;
  setAlternateLink('ru', next.canonical);
  setAlternateLink('x-default', next.canonical);
}

function setMeta(name: string, content: string): void {
  const el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (el) el.content = content;
}

function setPropertyMeta(property: string, content: string): void {
  const el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (el) el.content = content;
}

function setAlternateLink(hreflang: string, href: string): void {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = hreflang;
    document.head.appendChild(link);
  }
  link.href = href;
}

function setRouteStructuredData(data: Array<Record<string, unknown>> | null): void {
  const id = 'pdfka-route-structured-data';
  let script = document.querySelector<HTMLScriptElement>(`#${id}`);
  if (!data) {
    script?.remove();
    return;
  }
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
}

function homeStructuredData(): Array<Record<string, unknown>> {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: BRAND_NAME,
      url: `${SITE_ORIGIN}/`,
      inLanguage: 'ru',
      description: HOME_DESCRIPTION,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: BRAND_NAME,
      url: `${SITE_ORIGIN}/`,
      inLanguage: 'ru',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Any',
      isAccessibleForFree: true,
      description: 'ПДФ-инструменты онлайн для объединения, сжатия, разделения, подписи, организации и конвертации PDF без отправки файлов на сервер.',
      featureList: tools.map((tool) => tool.title),
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      publisher: {
        '@type': 'Organization',
        name: BRAND_NAME,
      },
    },
  ];
}

function toolStructuredData(page: SeoPage): Array<Record<string, unknown>> {
  const url = `${SITE_ORIGIN}/${page.slug}/`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'PDF-инструменты',
          item: `${SITE_ORIGIN}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: page.h1,
          item: url,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: page.h1,
      url,
      inLanguage: 'ru',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Any',
      isAccessibleForFree: true,
      description: page.description,
      keywords: [...new Set(page.primaryKeywords)].join(', '),
      featureList: page.steps,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      publisher: {
        '@type': 'Organization',
        name: BRAND_NAME,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: page.h1,
      description: page.lead,
      inLanguage: 'ru',
      totalTime: 'PT1M',
      step: page.steps.map((step, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        text: step,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: 'ru',
      mainEntity: page.faqs.map(([question, answer]) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      })),
    },
  ];
}

function setupThemeSwitch(root: HTMLElement) {
  const toggle = root.querySelector<HTMLButtonElement>('[data-theme-toggle]');
  if (!toggle) return;

  const sync = () => {
    const activeTheme = getTheme();
    const isDark = activeTheme === 'dark';
    toggle.setAttribute('aria-pressed', String(isDark));
    toggle.setAttribute('aria-label', isDark ? 'Включить светлый дизайн' : 'Включить тёмный дизайн');
    toggle.title = isDark ? 'Светлая тема' : 'Тёмная тема';
  };

  toggle.addEventListener('click', () => {
    setTheme(getTheme() === 'dark' ? 'light' : 'dark');
  });

  window.addEventListener('pdfka:theme-change', sync);
  sync();
}

function setupInstallAction(root: HTMLElement) {
  const modal = root.querySelector<HTMLElement>('[data-install-modal]');
  const title = root.querySelector<HTMLElement>('[data-install-title]');
  const lead = root.querySelector<HTMLElement>('[data-install-lead]');
  const steps = root.querySelector<HTMLOListElement>('[data-install-steps]');
  if (!modal || !title || !lead || !steps) return;

  let lastInstallTrigger: HTMLButtonElement | null = null;

  const installButtons = () =>
    Array.from(root.querySelectorAll<HTMLButtonElement>('[data-install-app]'));

  const isStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

  const sync = () => {
    const standalone = isStandalone();
    root.querySelectorAll<HTMLElement>('[data-install-cta]').forEach((cta) => {
      cta.hidden = standalone || cta.hasAttribute('data-install-cta-deferred');
    });

    const buttons = installButtons();
    if (!buttons.length) return;
    if (standalone) {
      buttons.forEach((button) => {
        button.hidden = true;
      });
      return;
    }

    const platform = detectInstallPlatform();
    const text = deferredInstallPrompt ? 'Установить' : platform === 'ios' ? 'На экран' : 'Сохранить';
    const hint = deferredInstallPrompt
      ? `Установить ${BRAND_NAME} как приложение`
      : platform === 'ios'
        ? `Добавить ${BRAND_NAME} на экран Домой`
        : `Сохранить ${BRAND_NAME} на этом устройстве`;
    buttons.forEach((button) => {
      button.hidden = false;
      button.title = hint;
      button.setAttribute('aria-label', hint);
      button.querySelector<HTMLElement>('[data-install-label]')?.replaceChildren(text);
    });
  };

  const showHelp = () => {
    const content = installHelpContent();
    title.textContent = content.title;
    lead.textContent = content.lead;
    steps.innerHTML = content.steps.map((step) => `<li>${escapeHtml(step)}</li>`).join('');
    modal.hidden = false;
    document.body.classList.add('install-modal-open');
    modal.querySelector<HTMLButtonElement>('[data-install-close]')?.focus();
  };

  const closeHelp = () => {
    modal.hidden = true;
    document.body.classList.remove('install-modal-open');
    const focusTarget = lastInstallTrigger && !lastInstallTrigger.hidden
      ? lastInstallTrigger
      : installButtons().find((installButton) => !installButton.hidden);
    focusTarget?.focus();
  };

  const runInstallAction = async (button: HTMLButtonElement) => {
    lastInstallTrigger = button;
    if (deferredInstallPrompt) {
      const promptEvent = deferredInstallPrompt;
      deferredInstallPrompt = null;
      await promptEvent.prompt();
      try {
        await promptEvent.userChoice;
      } catch {
        // Some Chromium builds reject userChoice when the prompt is interrupted.
      }
      sync();
      return;
    }
    showHelp();
  };

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest<HTMLButtonElement>('[data-install-app]');
    if (!button || !root.contains(button)) return;
    event.preventDefault();
    void runInstallAction(button);
  });

  root.querySelectorAll<HTMLElement>('[data-install-close]').forEach((closeButton) => {
    closeButton.addEventListener('click', closeHelp);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) closeHelp();
  });

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
    sync();
  });
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    sync();
  });
  window.addEventListener('pdfka:install-actions-changed', sync);
  window.matchMedia('(display-mode: standalone)').addEventListener('change', sync);
  sync();
}

function setupAnalyticsConsent(root: HTMLElement) {
  const banner = root.querySelector<HTMLElement>('[data-analytics-consent]');
  const accept = root.querySelector<HTMLButtonElement>('[data-analytics-accept]');
  if (!banner || !accept) return;

  const sync = () => {
    const visible = isMetrikaHost() && getAnalyticsConsent() !== 'accepted';
    banner.hidden = !visible;
  };

  accept.addEventListener('click', () => {
    setAnalyticsConsent('accepted');
    sync();
    initMetrika();
  });

  window.addEventListener('pdfka:analytics-consent-change', sync);
  sync();
}

function detectInstallPlatform(): 'ios' | 'android' | 'desktop' {
  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const isTouchMac = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (/iphone|ipad|ipod/i.test(userAgent) || isTouchMac) return 'ios';
  if (/android/i.test(userAgent)) return 'android';
  return 'desktop';
}

function installHelpContent(): { title: string; lead: string; steps: string[] } {
  const platform = detectInstallPlatform();
  if (platform === 'ios') {
    return {
      title: `Добавить ${BRAND_NAME} на экран Домой`,
      lead: 'iOS не разрешает сайту сделать это автоматически. Иконка добавляется через меню Safari.',
      steps: [
        'Откройте сайт в Safari.',
        'Нажмите кнопку «Поделиться».',
        'Выберите «На экран Домой» и подтвердите «Добавить».',
      ],
    };
  }
  if (platform === 'android') {
    return {
      title: `Установить ${BRAND_NAME} на Android`,
      lead: 'Если браузер не показал системную установку, добавьте сайт через меню Chrome.',
      steps: [
        'Откройте меню браузера.',
        'Нажмите «Установить приложение» или «Добавить на главный экран».',
        'Подтвердите добавление иконки.',
      ],
    };
  }

  const shortcut = /mac/i.test(navigator.platform || '') ? 'Cmd + D' : 'Ctrl + D';
  return {
    title: `Сохранить ${BRAND_NAME} на компьютере`,
    lead: 'Браузеры не разрешают сайту самому добавлять страницу в избранное или на панель закладок.',
    steps: [
      `Нажмите ${shortcut}.`,
      'Выберите панель закладок, если хотите видеть сайт сверху в браузере.',
      'В Chrome или Edge можно также установить сайт как приложение через иконку установки в адресной строке или меню браузера.',
    ],
  };
}

function renderHome(view: HTMLElement) {
  applyFullScreen(false);
  view.className = 'flex-1 w-full';

  const filters = homeToolFilters();

  view.innerHTML = `
    <div class="max-w-6xl mx-auto px-4 py-7">
      <div class="mb-5">
        <div>
          <h1 class="text-3xl sm:text-4xl font-extrabold mb-2">${HOME_H1}</h1>
          <p class="text-slate-400 text-sm">${HOME_LEAD}</p>
        </div>
      </div>

      <div id="hero-drop" class="mb-4"></div>
      <div id="hero-result" class="mb-6"></div>

      <div id="home-tools">
        <section>
          <div class="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
            <div>
              <h2 data-home-tools-title class="text-sm font-semibold text-slate-400 uppercase tracking-wider">PDF-инструменты</h2>
            </div>
            <div data-tool-filters class="flex flex-wrap gap-2" role="group" aria-label="Фильтр инструментов">
              ${filters.map((filter) => toolFilterButtonHtml(filter)).join('')}
            </div>
          </div>
          <div id="tool-grid" class="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            ${tools.map((t) => toolCardHtml(t)).join('')}
          </div>
          <p class="mt-5 max-w-4xl text-xs leading-relaxed text-slate-500">
            Все инструменты работают прямо в браузере: можно упорядочить страницы, объединить документы, сжать PDF, конвертировать файлы и добавить простые правки без загрузки документов на сервер.
          </p>
        </section>
      </div>

    </div>
  `;

  const toolController = setupHomeToolFilters(view);
  const dropHost = view.querySelector<HTMLElement>('#hero-drop')!;
  const resultHost = view.querySelector<HTMLElement>('#hero-result')!;
  setupHeroDrop(dropHost, resultHost, toolController);
  const selectedPdf = peekSelectedFiles().find((file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
  if (selectedPdf) {
    toolController.setPendingPdf(selectedPdf);
    renderPdfFileState(dropHost, selectedPdf, () => {
      clearSelectedFiles();
      resultHost.innerHTML = '';
      toolController.clearPendingFile();
      setupHeroDrop(dropHost, resultHost, toolController);
    }, (nextFiles) => handleHeroFiles(nextFiles, dropHost, resultHost, toolController));
  }
}

function setupHeroDrop(
  dropHost: HTMLElement,
  resultHost: HTMLElement,
  toolController: HomeToolController,
) {
  dropHost.onclick = null;
  dropHost.innerHTML = `
    <div id="hero-zone" class="upload-zone">
      <div class="mx-auto flex max-w-2xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex min-w-0 items-center gap-3">
          <div class="upload-zone-icon">↑</div>
          <div class="min-w-0">
            <div class="text-base sm:text-lg font-semibold leading-tight">Перетащите файл</div>
            <div class="mt-0.5 text-sm text-slate-500">PDF, Word, Excel, JPG, PNG, CSV</div>
          </div>
        </div>
        <button id="hero-pick" class="btn-primary text-sm shrink-0 px-5">Выбрать файл</button>
      </div>
      <input id="hero-input" type="file" class="hidden" multiple />
    </div>
  `;
  const zone = dropHost.querySelector<HTMLElement>('#hero-zone')!;
  const input = dropHost.querySelector<HTMLInputElement>('#hero-input')!;
  const pickBtn = dropHost.querySelector<HTMLButtonElement>('#hero-pick')!;

  pickBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    input.click();
  });
  zone.addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    const files = Array.from(input.files ?? []);
    if (files.length) handleHeroFiles(files, dropHost, resultHost, toolController);
    input.value = '';
  });
  ['dragenter', 'dragover'].forEach((ev) =>
    zone.addEventListener(ev, (e) => {
      e.preventDefault();
      zone.classList.add('border-brand', 'bg-slate-900');
    }),
  );
  ['dragleave', 'drop'].forEach((ev) =>
    zone.addEventListener(ev, (e) => {
      e.preventDefault();
      zone.classList.remove('border-brand', 'bg-slate-900');
    }),
  );
  zone.addEventListener('drop', (e) => {
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length) handleHeroFiles(files, dropHost, resultHost, toolController);
  });
}

function handleHeroFiles(
  files: File[],
  dropHost: HTMLElement,
  resultHost: HTMLElement,
  toolController: HomeToolController,
) {
  trackFileSelected(files);
  const route = routeFiles(files);
  if (route.kind === 'route') {
    clearSelectedFiles();
    toolController.clearPendingFile();
    setPendingFiles(files);
    location.hash = `#/${route.toolId}`;
    return;
  }
  if (route.kind === 'choose-pdf-action') {
    resultHost.innerHTML = '';
    setSelectedFiles([route.file]);
    toolController.setPendingPdf(route.file);
    renderPdfFileState(dropHost, route.file, () => {
      clearSelectedFiles();
      resultHost.innerHTML = '';
      toolController.clearPendingFile();
      setupHeroDrop(dropHost, resultHost, toolController);
    }, (nextFiles) => handleHeroFiles(nextFiles, dropHost, resultHost, toolController));
    return;
  }
  clearSelectedFiles();
  toolController.clearPendingFile();
  setupHeroDrop(dropHost, resultHost, toolController);
  resultHost.innerHTML = `
    <div class="rounded-lg border border-amber-900/40 bg-amber-950/20 p-4 text-sm text-amber-300">
      ${escapeHtml(route.details)}
    </div>
  `;
}

function renderPdfFileState(
  host: HTMLElement,
  file: File,
  onCancel: () => void,
  onReplace: (files: File[]) => void,
) {
  host.innerHTML = `
    <div class="rounded-lg border border-slate-800 bg-slate-900 p-4 sm:p-5 shadow-sm">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div class="flex min-w-0 items-start gap-3">
          <span class="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-brand text-xs font-extrabold text-white">PDF</span>
          <div class="min-w-0">
            <div class="text-xs text-slate-500 mb-1">Вы загрузили файл:</div>
            <div class="font-semibold leading-snug text-slate-100 break-words">${escapeHtml(file.name)}</div>
            <div class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
              <span>${formatFileSize(file.size)}</span>
              <span data-pdf-pages>считаем страницы…</span>
            </div>
          </div>
        </div>
        <div class="flex flex-wrap gap-2 lg:justify-end">
          <button id="pdf-replace" class="btn-secondary text-xs px-3 py-1.5">Заменить файл</button>
          <button id="pdf-cancel" class="btn-secondary text-xs px-3 py-1.5">Убрать</button>
          <input id="pdf-replace-input" type="file" class="hidden" multiple />
        </div>
      </div>
      <div class="mt-4 border-t border-slate-800 pt-3 text-sm text-slate-400">
        Выберите PDF-инструмент ниже. Файл откроется уже внутри выбранного инструмента.
      </div>
    </div>
  `;
  const replaceInput = host.querySelector<HTMLInputElement>('#pdf-replace-input')!;
  host.onclick = (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('#pdf-replace')) {
      replaceInput.click();
      return;
    }
    if (target.closest('#pdf-cancel')) onCancel();
  };
  replaceInput.addEventListener('change', () => {
    const files = Array.from(replaceInput.files ?? []);
    if (files.length) onReplace(files);
    replaceInput.value = '';
  });
  void updatePdfActionMetadata(host, file);
}

async function updatePdfActionMetadata(host: HTMLElement, file: File) {
  const pagesEl = host.querySelector<HTMLElement>('[data-pdf-pages]');
  if (!pagesEl) return;
  try {
    const [{ fileToBytes }, { openForRender }] = await Promise.all([
      import('./core/pdf-engine'),
      import('./core/render'),
    ]);
    const doc = await openForRender((await fileToBytes(file)).slice());
    pagesEl.textContent = `· ${formatPageCount(doc.numPages)}`;
    await doc.destroy();
  } catch {
    pagesEl.textContent = 'страницы не определены';
  }
}

type HomeToolFilter = { id: 'all' | ToolMeta['category']; label: string; count: number };
type HomeToolController = {
  setPendingPdf: (file: File) => void;
  clearPendingFile: () => void;
};

const homeCategoryOrder: ToolMeta['category'][] = ['pages', 'optimize', 'convert', 'edit', 'security'];
const pdfFileToolIds = new Set([
  'compress',
  'organize',
  'merge',
  'pdf-to-docx',
  'sign',
  'edit',
  'split',
  'rotate',
  'pdf-to-jpg',
  'pdf-to-txt',
]);

function homeToolFilters(): HomeToolFilter[] {
  const filters: HomeToolFilter[] = [{ id: 'all', label: 'Все', count: tools.length }];
  for (const category of homeCategoryOrder) {
    const count = tools.filter((tool) => tool.category === category).length;
    if (count) filters.push({ id: category, label: categoryLabels[category], count });
  }
  return filters;
}

function toolFilterButtonHtml(filter: HomeToolFilter): string {
  return `
    <button type="button" class="tool-filter" data-tool-filter="${filter.id}" aria-pressed="${filter.id === 'all'}">
      <span>${escapeHtml(filter.label)}</span>
      <span class="tool-filter-count">${filter.count}</span>
    </button>
  `;
}

function setupHomeToolFilters(view: HTMLElement): HomeToolController {
  const buttons = Array.from(view.querySelectorAll<HTMLButtonElement>('[data-tool-filter]'));
  const cards = Array.from(view.querySelectorAll<HTMLElement>('[data-tool-card]'));
  const filtersHost = view.querySelector<HTMLElement>('[data-tool-filters]');
  const title = view.querySelector<HTMLElement>('[data-home-tools-title]');
  let pendingPdf: File | null = null;
  let currentFilter = 'all';

  const applyFilter = (filter: string) => {
    currentFilter = pendingPdf ? 'all' : filter;
    for (const button of buttons) {
      button.setAttribute('aria-pressed', String(button.dataset.toolFilter === currentFilter));
    }
    for (const card of cards) {
      const matchesFilter = currentFilter === 'all' || card.dataset.toolCategory === currentFilter;
      const matchesPendingFile = !pendingPdf || card.dataset.pdfFileTool === 'true';
      const visible = matchesFilter && matchesPendingFile;
      card.classList.toggle('hidden', !visible);
    }
  };

  for (const button of buttons) {
    button.addEventListener('click', () => applyFilter(button.dataset.toolFilter ?? 'all'));
  }

  view.addEventListener('click', (event) => {
    if (!pendingPdf || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const card = target.closest<HTMLElement>('[data-tool-card]');
    if (!card || card.dataset.pdfFileTool !== 'true') return;
    setPendingFiles([pendingPdf]);
  });

  const syncMode = () => {
    const active = Boolean(pendingPdf);
    filtersHost?.classList.toggle('hidden', active);
    if (title) title.textContent = active ? 'Подходящие PDF-инструменты' : 'PDF-инструменты';
    applyFilter(active ? 'all' : currentFilter);
  };

  return {
    setPendingPdf(file: File) {
      pendingPdf = file;
      syncMode();
    },
    clearPendingFile() {
      pendingPdf = null;
      syncMode();
    },
  };
}

function toolCardHtml(t: ToolMeta): string {
  return `
    <a data-tool-card data-tool-category="${t.category}" data-tool-id="${t.id}" data-pdf-file-tool="${pdfFileToolIds.has(t.id)}" href="${toolHref(t.id)}" class="tool-card group min-h-[132px] justify-between">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="font-semibold leading-snug transition-colors group-hover:text-brand">${escapeHtml(t.title)}${toolBadgeHtml(t)}</div>
          <div class="mt-2 text-xs text-slate-400 leading-relaxed">${escapeHtml(t.description)}</div>
        </div>
        ${toolIconHtml(t)}
      </div>
    </a>
  `;
}

function toolHref(toolId: string): string {
  const page = seoPages.find((item) => item.appHash === toolId);
  return page ? `/${page.slug}/` : `#/${toolId}`;
}

function toolBadgeHtml(t: ToolMeta): string {
  return t.beta
    ? ' <span class="align-middle rounded border border-amber-800/70 bg-amber-950/30 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">beta</span>'
    : '';
}

function toolIconHtml(t: ToolMeta, size: 'sm' | 'md' = 'md'): string {
  const box = size === 'sm' ? 'w-8 h-8 text-base' : 'w-10 h-10 text-lg';
  return `
    <div class="${box} shrink-0 rounded-lg border border-slate-800 bg-slate-950/60 text-slate-500 grid place-items-center font-semibold transition-colors group-hover:border-brand/40 group-hover:bg-brand/5 group-hover:text-brand">
      ${escapeHtml(t.icon)}
    </div>
  `;
}

function seoSupportHtml(page: SeoPage): string {
  const enhancement = page.enhancement;

  return `
    <section class="mt-12 border-t border-slate-800 pt-8 space-y-4" aria-label="Подробнее">
      <div class="grid lg:grid-cols-3 gap-4">
        <div class="card">
          <h2 class="text-lg font-semibold mb-2">Как это работает</h2>
          <ol class="list-decimal pl-5 text-sm text-slate-400 space-y-1">
            ${page.steps.map((step) => `<li>${escapeHtml(step)}</li>`).join('')}
          </ol>
        </div>
        <div class="card lg:col-span-2">
          <h2 class="text-lg font-semibold mb-2">Частые вопросы</h2>
          <div class="grid sm:grid-cols-3 gap-3">
            ${page.faqs.map(([q, a]) => `
              <div>
                <h3 class="text-sm font-semibold leading-snug mb-1">${escapeHtml(q)}</h3>
                <p class="text-xs leading-relaxed text-slate-500">${escapeHtml(a)}</p>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
      ${enhancement?.sections?.length ? `
        <div class="grid md:grid-cols-2 gap-4">
          ${enhancement.sections.map((section) => `
            <section class="card">
              <h2 class="text-lg font-semibold mb-2">${escapeHtml(section.heading)}</h2>
              <p class="text-sm leading-relaxed text-slate-400">${escapeHtml(section.text)}</p>
            </section>
          `).join('')}
        </div>
      ` : ''}
      ${enhancement ? `
        <div class="card">
          <h2 class="text-lg font-semibold mb-2">${escapeHtml(enhancement.heading)}</h2>
          <p class="text-sm leading-relaxed text-slate-400 max-w-4xl">${escapeHtml(enhancement.text)}</p>
          <h3 class="text-sm font-semibold mt-4 mb-2">Частые задачи</h3>
          <ul class="list-disc pl-5 text-sm text-slate-400 space-y-1">
            ${enhancement.tasks.map((task) => `<li>${escapeHtml(task)}</li>`).join('')}
          </ul>
        </div>
      ` : ''}
      <div class="card">
        <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
          <div>
            <h2 class="text-lg font-semibold">Связанные инструменты</h2>
            <p class="mt-1 text-xs text-slate-500">Следующие действия для этого типа файла.</p>
          </div>
          <a href="/" class="text-sm text-slate-400 hover:text-brand">Все инструменты</a>
        </div>
        <div class="grid gap-2 sm:grid-cols-3">
          ${relatedToolLinksHtml(page)}
        </div>
      </div>
    </section>
  `;
}

function relatedToolLinksHtml(page: SeoPage): string {
  return page.relatedSlugs.map((slug) => {
    const relatedPage = seoPages.find((item) => item.slug === slug);
    const tool = relatedPage ? findTool(relatedPage.appHash) : undefined;
    if (!relatedPage || !tool) return '';
    return `
      <a data-related-tool href="/${relatedPage.slug}/" class="flex min-h-14 items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm text-slate-300 transition-colors hover:border-brand/30 hover:text-brand">
        ${toolIconHtml(tool, 'sm')}
        <span class="leading-snug">${escapeHtml(relatedPage.h1)}</span>
      </a>
    `;
  }).join('');
}

async function renderToolView(view: HTMLElement, tool: ToolMeta, seoPage?: SeoPage) {
  const fullscreen = tool.id === 'edit' && !seoPage;
  const title = seoPage?.h1 ?? tool.title;
  const description = seoPage?.lead ?? tool.description;
  const backHref = seoPage ? '/' : '/';
  const toolRootClass = seoPage && tool.id === 'edit'
    ? 'min-h-[720px] h-[calc(100vh-180px)] flex flex-col overflow-hidden rounded-lg border border-slate-800 bg-slate-900'
    : '';
  applyFullScreen(fullscreen);

  if (fullscreen) {
    view.className = 'tool-fullscreen';
    view.innerHTML = `
      <div class="px-4 pt-3 pb-2 flex items-center justify-between gap-3 border-b border-slate-800 shrink-0">
        <div class="flex items-center gap-3 min-w-0">
          <a href="/" class="text-sm text-slate-400 hover:text-slate-200 shrink-0">← Все инструменты</a>
          <h1 class="text-base font-bold truncate">${escapeHtml(tool.title)}${toolBadgeHtml(tool)}</h1>
        </div>
      </div>
      <div id="tool-root" class="flex-1 min-h-0 flex flex-col"></div>
    `;
  } else {
    view.className = 'flex-1 w-full';
    view.innerHTML = `
      <div class="max-w-6xl mx-auto px-4 py-8">
        <a href="${backHref}" class="text-sm text-slate-400 hover:text-slate-200 mb-4 inline-block">← Все инструменты</a>
        <h1 class="text-2xl sm:text-3xl font-bold mb-2">${escapeHtml(title)}${toolBadgeHtml(tool)}</h1>
        <p class="text-slate-400 mb-6">${escapeHtml(description)}</p>
        <div id="tool-root" class="${toolRootClass}"></div>
        ${seoPage ? seoSupportHtml(seoPage) : ''}
      </div>
    `;
  }

  const toolRoot = view.querySelector<HTMLElement>('#tool-root')!;

  toolRoot.innerHTML = `
    <div class="${fullscreen ? 'h-full' : 'card'} flex items-center justify-center py-12">
      <div class="flex items-center gap-3 text-slate-400">
        <span class="spinner spinner-lg"></span>
        <span>Открываем инструмент…</span>
      </div>
    </div>
  `;

  try {
    const mod = await tool.load();
    toolRoot.innerHTML = '';
    await mod.render(toolRoot);
  } catch (err) {
    console.error(err);
    trackToolError(err, 'tool_load');
    toolRoot.innerHTML = `
      <div class="card border-red-900/60 bg-red-950/20">
        <div class="font-semibold text-red-400 mb-2">Не удалось загрузить инструмент</div>
        <div class="text-sm text-slate-300 mb-3">${escapeHtml((err as Error).message)}</div>
        <div class="text-xs text-slate-500 mb-4">
          Проверьте подключение к интернету и попробуйте открыть инструмент ещё раз.
        </div>
        <button class="btn-secondary" id="retry">Повторить</button>
      </div>
    `;
    toolRoot.querySelector<HTMLButtonElement>('#retry')?.addEventListener('click', () =>
      renderToolView(view, tool),
    );
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(2)} МБ`;
}

function formatPageCount(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return `${count} страниц`;
  if (last === 1) return `${count} страница`;
  if (last >= 2 && last <= 4) return `${count} страницы`;
  return `${count} страниц`;
}

function applyFullScreen(on: boolean) {
  const footer = document.querySelector<HTMLElement>('#app-footer');
  if (footer) footer.style.display = on ? 'none' : '';
  document.body.classList.toggle('overflow-hidden', on);
}

async function renderSettingsRoute(view: HTMLElement) {
  applyFullScreen(false);
  view.className = 'flex-1 w-full';
  view.innerHTML = `<div class="max-w-4xl mx-auto px-4 py-8 flex items-center gap-3 text-slate-400"><span class="spinner"></span><span>Открываем настройки…</span></div>`;
  try {
    const mod = await import('./pages/settings');
    await mod.render(view);
  } catch (err) {
    view.innerHTML = `<div class="max-w-4xl mx-auto px-4 py-8 text-red-400">Не удалось загрузить настройки: ${(err as Error).message}</div>`;
  }
}

async function renderPrivacyRoute(view: HTMLElement) {
  applyFullScreen(false);
  view.className = 'flex-1 w-full';
  try {
    const mod = await import('./pages/privacy');
    await mod.render(view);
  } catch (err) {
    view.innerHTML = `<div class="max-w-4xl mx-auto px-4 py-8 text-red-400">Не удалось загрузить страницу: ${(err as Error).message}</div>`;
  }
}
